import React, { FC, useRef, useState } from "react";
import { format } from "date-fns";
import {
  Button,
  NotificationToastTypes,
  WithNotificationToast,
} from "component-library";
import { dataTestIds } from "data-testids";
import { ReactComponent as DownLoadIcon } from "component-library/src/images/svg/icons/download.svg";
import { escapeCSV } from "component-library/src/generics/escapeCSV";
import { useSearchSuggestions } from "../hooks/useSearchSuggestions";
import { ISearchCriteria, RoleStatus } from "../types";
import "./styles.scss";
import { captureExceptionEvent } from "shared-services";

interface Props {
  searchCriteria: ISearchCriteria;
  getProductName: (typeId: number) => string | undefined;
  translations: Record<string, string>;
}

const MAX_DOWNLOAD_SUGGESTIONS = 5000;

const DownloadSuggestionsButton: FC<React.PropsWithChildren<Props>> = ({
  searchCriteria,
  getProductName,
  translations,
}) => {
  const linkRef = useRef<HTMLAnchorElement>(null);
  const [showError, setShowError] = useState(false);
  const { refetch, isLoading } = useSearchSuggestions(
    {
      ...searchCriteria,
      skip: 0,
      take: MAX_DOWNLOAD_SUGGESTIONS,
    },
    false
  );

  const generateFile = async () => {
    try {
      const { data } = await refetch();
      const status = {
        [RoleStatus.Open]: translations.roleStatusOpen,
        [RoleStatus.Closed]: translations.roleStatusClosed,
        [RoleStatus.Cast]: translations.roleStatusCast,
      };

      const headerLabels = [
        translations.productionTitleLabel,
        translations.typeLabel,
        translations.roleLabel,
        translations.sentByLabel,
        translations.suggestedLabel,
        translations.roleStatusLabel,
      ];
      const rows =
        data?.suggestions.map((suggestion) => [
          suggestion.confidential
            ? translations.confidentialProduction
            : escapeCSV(suggestion.productionTitle),
          getProductName(suggestion.type),
          escapeCSV(suggestion.role),
          escapeCSV(suggestion.suggestedBy),
          format(new Date(suggestion.suggested), "dd/MM/yyyy"),
          status[suggestion.statusID],
        ]) || [];

      const fileContent = [headerLabels, ...rows]
        .map((row) => row.join(","))
        .join("\r\n");
      const url = URL.createObjectURL(new Blob(["\ufeff", fileContent]));

      linkRef.current?.setAttribute("href", url);
      linkRef.current?.setAttribute(
        "download",
        `my_suggestions_${format(new Date(), "dd/MM/yyyy")}.csv`
      );
      linkRef.current?.click();
    } catch (e) {
      captureExceptionEvent(e);
      setShowError(true);
    }
  };

  return (
    <div className="w-[50%]">
      <WithNotificationToast
        handleHide={() => setShowError(false)}
        show={showError}
        type={NotificationToastTypes.Error}
        text={translations.downloadErrorText}
      >
        <Button
          type="secondary"
          text={
            <div className="flex items-center gap-[5px]">
              <DownLoadIcon />
              {translations.download}
            </div>
          }
          className="download-button"
          loading={isLoading}
          onClick={generateFile}
        />
      </WithNotificationToast>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        ref={linkRef}
        aria-label="Download suggestions"
        data-testid={
          dataTestIds.nuJobsApp[
            "MySuggestionsView.MySuggestionsView.downloadSuggestionsButton"
          ]
        }
        hidden
      />
    </div>
  );
};

export default DownloadSuggestionsButton;
